import moment from 'moment';
import { EscortAppPatientEnum, RateTypesEnum } from 'fragments';

const STATUS_PRIORITY = [
  EscortAppPatientEnum.OPEN,
  EscortAppPatientEnum.ASSIGNED,
  EscortAppPatientEnum.ARRIVED,
  EscortAppPatientEnum.EN_ROUT,
  EscortAppPatientEnum.COMPLETED,
  EscortAppPatientEnum.NO_SHOW,
  EscortAppPatientEnum.CANCELLED,
];

const LOS_PRIORITY = [RateTypesEnum.WC, RateTypesEnum.GURNEY, RateTypesEnum.WALKER];

export function sortLegs(data, sortType, orderType, duplicate, dialysisOnly) {
  const PRIORITY = {
    STATUS: [...STATUS_PRIORITY],
    LOS: [...LOS_PRIORITY],
  };

  const getValueByPath = (obj, path) => path?.split('.')?.reduce((acc, key) => acc?.[key], obj);

  const getCompareValue = (item, path) => {
    const value = getValueByPath(item, path);

    if (path === 'patient') {
      const firstName = value?.firstName || '';
      const lastName = value?.lastName || '';
      return `${lastName} ${firstName}`.toLowerCase();
    }

    if (path === 'payer') return (value?.displayName || value?.name)?.toLowerCase() || '';
    // Legs carry their own frozen LOS; the profile is only a fallback for legs created before it existed.
    if (path === 'los') return (value ?? item?.patient?.los)?.toLowerCase() || '';
    if (path === 'vehicle') return value?.unitNumber?.toString() || '';

    if (path?.includes('Time') || path?.includes('Date')) {
      const date = moment(value);
      return date.isValid() ? date.valueOf() : Number.MAX_SAFE_INTEGER;
    }

    if (typeof value === 'number') return value;
    if (typeof value === 'string') return value.toLowerCase().trim();
    return String(value || '');
  };

  let filteredData = data;
  if (duplicate) filteredData = filteredData.filter((item) => item?.isDuplicate);
  if (dialysisOnly) filteredData = filteredData.filter((item) => item?.dialysis);

  return [...filteredData].sort((a, b) => {
    const aScheduled = a.pickUp?.scheduledTime ? moment(a.pickUp.scheduledTime) : null;
    const bScheduled = b.pickUp?.scheduledTime ? moment(b.pickUp.scheduledTime) : null;

    const aStatusRank = PRIORITY.STATUS.indexOf(a.status);
    const bStatusRank = PRIORITY.STATUS.indexOf(b.status);
    const safeAStatus = aStatusRank !== -1 ? aStatusRank : Number.MAX_SAFE_INTEGER;
    const safeBStatus = bStatusRank !== -1 ? bStatusRank : Number.MAX_SAFE_INTEGER;

    let result = 0;

    if (!sortType) {
      if (safeAStatus !== safeBStatus) return safeAStatus - safeBStatus;

      const aMinutes = aScheduled ? aScheduled.hour() * 60 + aScheduled.minute() : Number.MAX_SAFE_INTEGER;
      const bMinutes = bScheduled ? bScheduled.hour() * 60 + bScheduled.minute() : Number.MAX_SAFE_INTEGER;
      return aMinutes - bMinutes;
    } else if (sortType === 'statuses') {
      if (!orderType || orderType === 'ASC') {
        if (safeAStatus !== safeBStatus) return safeAStatus - safeBStatus;

        const aMinutes = aScheduled ? aScheduled.hour() * 60 + aScheduled.minute() : Number.MAX_SAFE_INTEGER;
        const bMinutes = bScheduled ? bScheduled.hour() * 60 + bScheduled.minute() : Number.MAX_SAFE_INTEGER;
        return aMinutes - bMinutes;
      } else {
        if (safeAStatus !== safeBStatus) return safeBStatus - safeAStatus;

        const aMinutes = aScheduled ? aScheduled.hour() * 60 + aScheduled.minute() : Number.MAX_SAFE_INTEGER;
        const bMinutes = bScheduled ? bScheduled.hour() * 60 + bScheduled.minute() : Number.MAX_SAFE_INTEGER;
        return bMinutes - aMinutes;
      }
    } else {
      const aValue = getCompareValue(a, sortType);
      const bValue = getCompareValue(b, sortType);

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        result = aValue.localeCompare(bValue);
      } else {
        result = aValue - bValue;
      }
    }

    return !orderType || orderType === 'ASC' ? result : -result;
  });
}
